import { BehaviorSubject } from 'rxjs'

import {
  DRAWER_DESKTOP_LEFT_OFFSET,
  DRAWER_SIDEBAR_WIDTH,
  getDesktopDrawerContentWidth
} from '@/presentation/modules/Base/components/Drawer/Drawer.constants'
import { DrawerContentWidthVariant } from '@/presentation/modules/Base/components/Drawer/Drawer.types'

export interface ScreenElementsStateProps {
  drawer: boolean
  drawerContentWidthVariant: DrawerContentWidthVariant
  toolPanel: boolean
  meteorologyControl: boolean
  featureInfoPanel: boolean
  chat: boolean
  boundsDefaultPadding: {
    top: number
    right: number
    bottom: number
    left: number
  }
}

const elementDistance = {
  open: {
    top: 80,
    right: 375,
    bottom: 20 + 58 + 16 + 36
  },
  close: {
    top: 0,
    right: 30 + 40,
    bottom: 23 + 40,
    left: DRAWER_SIDEBAR_WIDTH + DRAWER_DESKTOP_LEFT_OFFSET
  }
}

function getDrawerLeftPadding(drawer: boolean, drawerContentWidthVariant: DrawerContentWidthVariant): number {
  if (!drawer) {
    return elementDistance.close.left
  }

  return getDesktopDrawerContentWidth(drawerContentWidthVariant) + DRAWER_DESKTOP_LEFT_OFFSET + DRAWER_SIDEBAR_WIDTH
}

function getBoundsDefaultPadding(state: ScreenElementsStateProps): ScreenElementsStateProps['boundsDefaultPadding'] {
  return {
    top: elementDistance.open.top,
    right:
      state.toolPanel || state.featureInfoPanel || state.chat
        ? elementDistance.open.right
        : elementDistance.close.right,
    bottom: state.meteorologyControl ? elementDistance.open.bottom : elementDistance.close.bottom,
    left: getDrawerLeftPadding(state.drawer, state.drawerContentWidthVariant)
  }
}

function resolveState(state: ScreenElementsStateProps): ScreenElementsStateProps {
  return {
    ...state,
    boundsDefaultPadding: getBoundsDefaultPadding(state)
  }
}

const INITIAL_STATE = resolveState({
  drawer: false,
  drawerContentWidthVariant: 'default',
  toolPanel: false,
  meteorologyControl: false,
  featureInfoPanel: false,
  chat: false,
  boundsDefaultPadding: {
    top: 0,
    right: 0,
    bottom: 0,
    left: 0
  }
})

export const ScreenElementsState = new BehaviorSubject<ScreenElementsStateProps>(INITIAL_STATE)

// Padding (top, right, bottom, left) para flyTo que respeita a área útil do mapa, descontando
// os painéis abertos. Lê o estado ao vivo (getValue()) para refletir um painel recém-aberto na
// mesma tarefa síncrona. Espelha o padrão usado em WrittenSearchPresenter/MapView.
export function getFeatureFlyToPadding(): [number, number, number, number] {
  const { boundsDefaultPadding } = ScreenElementsState.getValue()
  return [
    40 + boundsDefaultPadding.top,
    75 + boundsDefaultPadding.right,
    40 + boundsDefaultPadding.bottom,
    75 + boundsDefaultPadding.left
  ]
}

const setState = (patch: Partial<ScreenElementsStateProps>) => {
  ScreenElementsState.next(resolveState({ ...ScreenElementsState.getValue(), ...patch }))
}

export const ScreenElementsStateMutator = {
  mutate: (newState: ScreenElementsStateProps) => {
    ScreenElementsState.next(resolveState(newState))
  },

  setDrawer: (drawer: boolean) => {
    if (ScreenElementsState.getValue().drawer === drawer) return
    setState({ drawer })
  },

  setDrawerContentWidthVariant: (drawerContentWidthVariant: DrawerContentWidthVariant) => {
    if (ScreenElementsState.getValue().drawerContentWidthVariant === drawerContentWidthVariant) return
    setState({ drawerContentWidthVariant })
  },

  setToolPanel: (toolPanel: boolean) => {
    if (ScreenElementsState.getValue().toolPanel === toolPanel) return
    setState({ toolPanel })
  },

  setFeatureInfoPanel: (featureInfoPanel: boolean) => {
    if (ScreenElementsState.getValue().featureInfoPanel === featureInfoPanel) return
    setState({ featureInfoPanel })
  },

  setMeteorologyControl: (meteorologyControl: boolean) => {
    if (ScreenElementsState.getValue().meteorologyControl === meteorologyControl) return
    setState({ meteorologyControl })
  },

  setChat: (chat: boolean) => {
    if (ScreenElementsState.getValue().chat === chat) return
    setState({ chat })
  },

  getDrawerState: (): 'closed' | 'open' | 'expanded' => {
    const { drawer, drawerContentWidthVariant } = ScreenElementsState.getValue()

    if (!drawer) {
      return 'closed'
    }

    if (drawerContentWidthVariant === 'adhp-expanded') {
      return 'expanded'
    }

    return 'open'
  }
}
